import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Job from '../../../../models/job.js';
import definition from '@job-schemas/public-ledger/sign-mismatch.json';
import { jobSchema } from '../../job-schema.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import { signMismatchReport } from '../../../../helpers/public-ledger-sign-mismatch.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';

/**
 * The PUBLIC_LEDGER team's urgent card when a signatory's browser refused to sign a public ledger transaction because what
 * nodeStream served didn't match what the card showed (#24, helpers/public-ledger-sign-check.ts). Nothing was signed.
 * public-ledger/report-sign-mismatch raises one per signing workflow and adds each later reporter to it. Everything shown
 * comes from the job metadata: the team can't read the company's rows. The team finds the cause, then marks it resolved.
 */
@customElement(NSPublicLedgerSignMismatch.is)
export default class NSPublicLedgerSignMismatch extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-sign-mismatch';
  }

  static get schema(): JobSchema {
    // Resolved by the card's own button, once claimed
    return jobSchema(definition, { outcomes: { resolved: { type: 'hidden' } }, icon: 'transaction' });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        margin: 0 0 var(--wa-space-m);
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .ids {
        overflow-wrap: anywhere;
      }
    `,
  ];

  @state()
  private __busy = false;

  async connectedCallback() {
    await super.connectedCallback();
    if (!this._job) return;
    // A later reporter is added to the card while it may be open
    const jobId = this._job.id;
    this._subscriptions.subscribe(`job.${jobId}.*`, () => {
      const updated = this._db?.get(`job.${jobId}`) as Job | undefined;
      if (updated) this._job = updated;
    });
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __resolved() {
    if (this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome('resolved');
    } catch (err) {
      this.showError(err, 'Unable to mark this resolved. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const report = signMismatchReport(this._job?.metadata);
    const reporters = report.reportedBy.map((r) => r.name ?? r.personId);
    return html`
      <ns-job-layout>
        <p slot="intro">
          A signatory's browser refused to sign a public ledger transaction because it didn't match what their card
          showed. Nothing was signed.
        </p>
        <div class="job-panel">
          <h3>The transaction</h3>
          <ns-detail-list
            class="ids"
            .rows=${[
              { label: 'Company', value: report.companyName ?? report.companyId ?? undefined },
              { label: 'Company ID', value: report.companyId ?? undefined },
              { label: 'Type', value: report.transactionType ?? undefined },
              { label: 'Transaction ID', value: report.transactionId ?? undefined },
              { label: 'Signing workflow ID', value: report.signingWorkflowId ?? undefined },
              { label: 'Reported by', value: reporters.length ? reporters.join(', ') : undefined },
            ]}
          ></ns-detail-list>
          <h3>Why it was refused</h3>
          <p>${report.reasonText ?? "The browser gave a reason this version of nodeStream doesn't know."}</p>
          ${report.reason
            ? html`
                <p>Reason code: ${report.reason}</p>
              `
            : nothing}
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What to do</h3>
          <p>
            Find out why the transaction nodeStream served didn't match its own records: a fault in how it was built, or
            a change made to it on the way. The browser checks every transaction before it signs, so no signature was
            added.
          </p>
          <p>
            The signing request stays open, and other signatories' browsers will refuse it too while it stays wrong.
            Mark this resolved once you know the cause.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__resolved()}>
        Resolved
      </wa-button>
    `;
  }
}
